import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    StatsLeadersPosFilters,
    StatsLeadersTeamsFilters
} from './components/filters';
import {
    Heading,
    BenchmarkingSwitch,
    DataTypeToggle,
    dataTypeOptions
} from './components/header';
import {
    CategoryNavItem,
    SeasonNavItem,
    RoundNavItem
} from './components/nav-items';
import { StatsLeadersTable } from './components/table';
import {
    BenchmarkingExplainer,
    BenchmarkingLegend
} from 'common/react/components/benchmarking';
import {
    statsLeadersBenchmarkingSelector,
    statsLeadersNavSelector,
    statsLeadersFiltersSelector,
    statsLeadersDataTypeSelector
} from 'store/modules/statspro/stats-leaders/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useStatsLeadersNavSync } from './hooks/use-stats-leaders-nav-sync';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { POSITIONS } from './config/positions';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { FilterButton } from 'common/react/components/FilterButton';
import { ResponsiveRoundNavItem } from './components/nav-items/ResponsiveRoundNavItem';
import {
    UPDATE_NAV_ITEM,
    UPDATE_DATA_TYPE,
    UPDATE_BENCHMARKING,
    UPDATE_FILTER_ITEM
} from 'store/modules/statspro/stats-leaders/actions';
import { getTranslation } from 'common/react/utils/translations';

const MAXIMUM_SELECTIONS = 5;
const COUNTER_THRESHOLD = 2;
const INITIAL_CATEGORY = 'Key Stats';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown rounded-filter-wrapper">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * @param root0
 * @param root0.compseasonId
 * @param root0.competitionId
 * @param root0.competitionPid
 * @param root0.showBenchmarkingAflw
 * @param root0.counterThreshold
 * @param root0.maximumSelections
 * @param root0.title
 * @returns {React.JSX.Element} Component.
 */
export const StatsLeaders = ({
    compseasonId,
    competitionId,
    competitionPid,
    showBenchmarkingAflw,
    title,
    maximumSelections = MAXIMUM_SELECTIONS,
    counterThreshold = COUNTER_THRESHOLD
}) => {
    const dispatch = useDispatch();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);

    const initialValues = {
        competitionId,
        compseasonId,
        competitionPid
    };
    const benchmarking = useSelector(statsLeadersBenchmarkingSelector);
    const dataType = useSelector(statsLeadersDataTypeSelector);

    const nav = useSelector(statsLeadersNavSelector);
    const filters = useSelector(statsLeadersFiltersSelector);
    const options = useCompetitionNavigationOptions(nav);
    useStatsLeadersNavSync(options, competitionId, compseasonId, initialValues);

    const windowSize = useWindowSizeV2();
    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const showBenchmarkingAFLWBool = showBenchmarkingAflw === 'true';
    const { WOMENS } = PULSE.app.common.CONSTANTS.PROVIDER_IDS;
    const isAFLW = competitionPid === WOMENS;

    const isAFLM =
        competitionPid === PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP;

    const showBenchmarking =
        (!isAFLW || (isAFLW && showBenchmarkingAFLWBool)) &&
        (dataType?.type === 'averages' ||
            dataType?.type === 'benchmarkedAverages');

    const statsLeadersRef = useRef();

    // Reset functions for multi-select filters
    const resetPositions = () => {
        dispatch(
            UPDATE_FILTER_ITEM.request({
                filterItem: 'positions',
                value: []
            })
        );
    };

    const resetTeams = () => {
        dispatch(
            UPDATE_FILTER_ITEM.request({
                filterItem: 'teams',
                value: []
            })
        );
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }

        // Check if navigation items have changed from defaults
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;
        const isSeasonChanged = nav?.season?.id !== nav?.season?.initialItemId;
        const isRoundChanged = nav?.round?.id !== -1; // -1 indicates entire season
        const isCategoryChanged = nav?.category !== INITIAL_CATEGORY; // Add category check

        // Check if filters have changed from defaults
        const hasPositionFilters =
            filters.positions && filters.positions.length > 0;
        const hasTeamFilters = filters.teams && filters.teams.length > 0;

        // Check if data type has changed from default
        const hasDataTypeChanged = dataType?.type !== 'totals';

        // Check if benchmarking has changed from default
        const hasBenchmarkingChanged = benchmarking?.isActive !== false;

        return (
            isCompetitionChanged ||
            isSeasonChanged ||
            isRoundChanged ||
            isCategoryChanged ||
            hasPositionFilters ||
            hasTeamFilters ||
            hasDataTypeChanged ||
            hasBenchmarkingChanged
        );
    };

    const resetToDefaults = () => {
        // Reset navigation items to initial values
        const initialCompetitionId =
            nav?.competition?.initialItemId || initialValues.competitionId;
        const initialSeasonId =
            nav?.season?.initialItemId || initialValues.compseasonId;
        const initialRoundId = nav?.round?.initialItemId || -1;

        if (nav?.competition?.id !== initialCompetitionId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: { id: initialCompetitionId }
                })
            );
        }

        if (nav?.category !== INITIAL_CATEGORY) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'category',
                    value: INITIAL_CATEGORY
                })
            );
        }

        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }

        if (nav?.round?.id !== initialRoundId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'round',
                    value: { id: initialRoundId, number: 0 }
                })
            );
        }

        // Reset filters using the reset functions
        if (filters.positions && filters.positions.length > 0) {
            resetPositions();
        }

        if (filters.teams && filters.teams.length > 0) {
            resetTeams();
        }

        // Reset data type to totals
        if (dataType?.type !== 'totals') {
            dispatch(
                UPDATE_DATA_TYPE.request({
                    type: 'totals'
                })
            );
        }

        // Reset benchmarking
        if (benchmarking?.isActive !== false) {
            dispatch(
                UPDATE_BENCHMARKING.request({
                    isActive: false
                })
            );
        }
    };

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            position: filters.positions.map((pos) => ({ id: pos })),
            filters: {
                teams: filters.teams
            },
            round: nav.round,
            dataType,
            benchmarking
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions,
            positions: POSITIONS.map((pos) => ({
                value: pos.keys[0],
                label: pos.name
            }))
        },
        filtersFields: [
            'category',
            'season',
            'round',
            'position',
            'teams',
            'benchmarking',
            'dataType'
        ],
        filtersRef: statsLeadersRef
    });

    return (
        <div ref={statsLeadersRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CategoryNavItem
                            classes="fixtures-nav__comp-select"
                            title={title}
                            hideLabel={true}
                        />
                        {showResponsiveItems && (
                            <FilterButton
                                classes="fixtures-nav__filter-button"
                                onChange={() =>
                                    setAreFilterOptionsVisible((prev) => !prev)
                                }
                                areFilterOptionsVisible={
                                    areFilterOptionsVisible
                                }
                                resetFilterBtnStyles={resetFilterBtnStyles}
                                setResetFilterBtnStyles={
                                    setResetFilterBtnStyles
                                }
                                showFilterLabel={false}
                                showActiveCircle
                            />
                        )}
                    </StatsSection.NavItems>
                    {(!showResponsiveItems || areFilterOptionsVisible) && (
                        <StatsSection.NavFilterItems
                            title={title}
                            filterKeys={[
                                'seasons',
                                'rounds',
                                'positions',
                                'teams'
                            ]}
                        >
                            <SeasonNavItem title={title} />
                            {showResponsiveItems ? (
                                <ResponsiveRoundNavItem
                                    selector={statsLeadersNavSelector}
                                    removeFinals
                                    hideLabel
                                />
                            ) : (
                                <RoundNavItem title={title} />
                            )}
                            <StatsLeadersPosFilters
                                title={title}
                                maximumSelections={maximumSelections}
                                counterThreshold={counterThreshold}
                                addOnResetCallback={() => resetPositions}
                            />
                            <StatsLeadersTeamsFilters
                                title={title}
                                maximumSelections={maximumSelections}
                                counterThreshold={counterThreshold}
                                addOnResetCallback={() => resetTeams}
                            />
                            {hasChangedFromDefaults() && (
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            )}
                        </StatsSection.NavFilterItems>
                    )}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentHeader>
                        <StatsSection.ContentHeaderHeading>
                            <Heading />
                        </StatsSection.ContentHeaderHeading>
                        <StatsSection.ContentHeaderOptions>
                            {showBenchmarking && <BenchmarkingSwitch />}
                            <DataTypeToggle />
                        </StatsSection.ContentHeaderOptions>
                        <StatsSection.ContentHeaderBelow>
                            {benchmarking?.isActive && showBenchmarking && (
                                <BenchmarkingLegend />
                            )}
                            {benchmarking?.isActive && showBenchmarking && (
                                <BenchmarkingExplainer />
                            )}
                        </StatsSection.ContentHeaderBelow>
                    </StatsSection.ContentHeader>
                    <StatsSection.ContentBody>
                        <StatsLeadersTable isAFLW={isAFLW} isAFLM={isAFLM} />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

StatsLeaders.propTypes = {
    compseasonId: PropTypes.string.isRequired,
    competitionId: PropTypes.string.isRequired,
    competitionPid: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    title: PropTypes.string,
    maximumSelections: PropTypes.number,
    counterThreshold: PropTypes.number
};
